import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { isUnchanged, quantile, symlog } from "../../shared/chartMath";
import { percentage, percentageNoDecimals } from "../../shared/percentage";
import {
  TABULAR_NUMERALS,
  Tone,
  toneColors,
} from "../general/dataAtoms/shared.ts";
import { captureTone } from "./captureTone.ts";
import { ANNOTATION_FONT_SIZE_PX } from "./scatter/scatterDataset.ts";
import {
  REFERENCE_LINE_DASH,
  REFERENCE_LINE_WIDTH,
  ScatterAxis,
  ScatterOverlay,
  ScatterZone,
  toneZoneColors,
  ZonedScatter,
} from "./scatter/ZonedScatter.tsx";
import {
  SettlementMark,
  SettlementRangeStrip,
} from "./SettlementRangeStrip.tsx";

export interface QualityPoint {
  caseId: string;
  caseName: string;
  // who the client settled with — for the tooltip; absent on a record stored
  // before the opponent was kept
  opponentName?: string;
  // x: where the settlement landed in the rational range — 0 = our EV (floor),
  // 0.5 = midpoint (even split), 1 = opponent's EV (their ceiling). May be < 0
  // (settled below our EV) or > 1 (beyond the opponent's ceiling), without bound.
  capture: number;
  // the figure the case settled at (client perspective) — for the tooltip
  settledFigure: number;
  // the range anchors and its midpoint, client perspective — for the tooltip:
  // the client's EV, the end of the opponent's bargaining range (the best
  // settled figure the opponent should rationally accept: its EV, negated),
  // and the midpoint
  partyEv: number;
  opponentRangeEnd: number;
  mid: number;
  // headline claim value (drives y)
  claimValue: number;
  // y: claim value normalised 0…1 over all settled cases (bigger claim = higher)
  claimNorm: number;
}

// Symmetric-log transform centred on the even split (50%): the bargaining range
// [0%, 100%] maps to a LINEAR central zone (|capture − 0.5| ≤ 0.5), so the
// 0/50/100 anchors stay well spread and readable; settlements beyond the range
// (below our EV or above the opponent's) are log-compressed into the tails, so
// extreme cases show at their true position instead of clipping or piling.
const LINTHRESH = 0.5;
const symmetricLog = symlog(LINTHRESH);
const forward = (capture: number): number =>
  symmetricLog.forward(capture - 0.5);
const inverse = (y: number): number => symmetricLog.inverse(y) + 0.5;
// candidate axis ticks, in capture fractions (filtered to the visible range);
// kept sparse so the log-compressed tails don't crowd
const CANDIDATE_TICKS = [-10, -2, -1, 0, 0.5, 1, 2, 10];

const toXY = (p: QualityPoint) => ({ x: forward(p.capture), y: p.claimNorm });
const formatTick = (value: number) =>
  percentageNoDecimals.format(inverse(value));

// the zones in the order of the divided bar: below our EV | conceded | beat
// the even split
const ZONES: readonly { tone: Tone; label: string }[] = [
  { tone: "adverse", label: "belowFloor" },
  { tone: "neutral", label: "conceded" },
  { tone: "favourable", label: "beatSplit" },
];

/**
 * Settlement-quality scatter: one dot per settlement, x = where the agreed
 * figure landed within the rational bargaining range (0% = our EV, 50% = the
 * even split, 100% = the opponent's EV) on a symmetric-log scale, y = claim
 * size. Further right is better for the client. Dots are coloured in three
 * zones — adverse below our own EV (worse than litigating), neutral between
 * our EV and the even split (rational but conceded), favourable from the even
 * split on. The header carries the median and average capture; a three-way
 * divided bar the counts.
 */
export const SettlementQualityChart = ({
  title,
  subtitle,
  points,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  points: QualityPoint[];
  // shown in place of the chart when there is nothing to plot
  emptyMessage: string;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.quality",
  });

  const stats = useMemo(() => {
    const caps = points.map((p) => p.capture).sort((a, b) => a - b);
    const median = quantile(caps, 0.5);
    const avg = caps.reduce((s, c) => s + c, 0) / (caps.length || 1);
    // axis bounds in transformed space; always include 0%…100%
    const lo = Math.min(0, caps[0] ?? 0);
    const hi = Math.max(1, caps[caps.length - 1] ?? 1);
    const axisMin = forward(lo) - 0.08;
    const axisMax = forward(hi) + 0.08;
    const ticks = CANDIDATE_TICKS.map(forward).filter(
      (v) => v >= axisMin && v <= axisMax,
    );
    return { median, avg, axisMin, axisMax, ticks };
  }, [points]);

  const zoneColors = useMemo(() => toneZoneColors(theme), [theme]);

  const zones = useMemo<ScatterZone<QualityPoint>[]>(
    () =>
      ZONES.map(({ tone, label }) => {
        const inZone = points.filter((p) => captureTone(p.capture) === tone);
        return {
          key: tone,
          points: inZone,
          color: zoneColors[tone],
          label: t(label, { count: inZone.length }),
        };
      }),
    [points, zoneColors, t],
  );

  const xAxis = useMemo<ScatterAxis>(
    () => ({
      min: stats.axisMin,
      max: stats.axisMax,
      // fixed ticks at nice capture values (symmetric-log positions)
      ticks: stats.ticks,
      formatTick,
    }),
    [stats],
  );

  // the three labelled anchor lines: our EV (0%), even split (50%), opp. EV (100%)
  const drawAnchors: ScatterOverlay = ({ ctx, chartArea, scales }) => {
    const xScale = scales.x;
    if (!xScale) return;
    const { top, bottom } = chartArea;
    ctx.save();
    ctx.font = `600 ${ANNOTATION_FONT_SIZE_PX}px ${theme.typography.fontFamily}`;
    ctx.textAlign = "center";
    ctx.lineWidth = REFERENCE_LINE_WIDTH;
    ctx.strokeStyle = theme.palette.text.secondary;
    ctx.fillStyle = theme.palette.text.secondary;
    // the even split is the line the zones are told apart by: it alone is solid
    const anchor = (capture: number, label: string, solid = false) => {
      const x = xScale.getPixelForValue(forward(capture));
      ctx.setLineDash(solid ? [] : REFERENCE_LINE_DASH);
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      ctx.fillText(label, x, top - 6);
    };
    anchor(0, t("ourEv"));
    anchor(0.5, t("evenSplit"), true);
    anchor(1, t("opponentEv"));
    ctx.restore();
  };

  // the other side as a sentence of the tooltip names it: by name, or
  // generically for a record stored before the opponent was kept
  const opponentOf = ({ opponentName }: QualityPoint) =>
    opponentName ?? t("opponentFallback");

  // plain-language position of a settlement, as a £ distance: relative to the
  // client's EV or the end of the opponent's bargaining range when it landed
  // outside the range, otherwise to the midpoint
  const describeSettlement = (point: QualityPoint) => {
    const { capture, partyEv, opponentRangeEnd, mid, settledFigure } = point;
    if (captureTone(capture) === "adverse")
      return t("settledBelowClientEv", {
        amount: currency.format(partyEv - settledFigure),
      });
    if (capture > 1)
      return t("settledAboveOpponentRange", {
        amount: currency.format(settledFigure - opponentRangeEnd),
        opponent: opponentOf(point),
      });
    const diff = settledFigure - mid;
    if (isUnchanged(diff)) return t("settledAtMid");
    return diff > 0
      ? t("settledAboveMid", {
          amount: currency.format(diff),
          mid: currency.format(mid),
        })
      : t("settledBelowMid", {
          amount: currency.format(-diff),
          mid: currency.format(mid),
        });
  };

  const tones = toneColors(theme);
  // a capture that is neither good nor bad stays in ink
  const statColor = (capture: number) => {
    const tone = captureTone(capture);
    return tone === "neutral" ? undefined : tones[tone];
  };

  return (
    <ZonedScatter
      title={title}
      subtitle={subtitle}
      ariaLabel={t("chartLabel")}
      stats={[
        {
          label: t("median"),
          value: percentage.format(stats.median),
          color: statColor(stats.median),
        },
        {
          label: t("average"),
          value: percentage.format(stats.avg),
          color: statColor(stats.avg),
        },
      ]}
      zones={zones}
      toXY={toXY}
      xAxis={xAxis}
      drawOverlay={drawAnchors}
      overlayHasTopLabels
      emptyMessage={emptyMessage}
      renderTooltip={(point) => {
        const tone = captureTone(point.capture);
        const figures = [
          // what was agreed carries the dot it has on the strip below
          {
            label: t("settledFor"),
            value: point.settledFigure,
            mark: <SettlementMark color={zoneColors[tone]} />,
          },
          { label: t("clientEv"), value: point.partyEv },
          {
            label: point.opponentName
              ? t("namedOpponentEv", { opponent: point.opponentName })
              : t("opponentEv"),
            // the opponent's own EV, as the opponent has it
            value: -point.opponentRangeEnd,
          },
        ];
        return (
          <>
            <Box sx={{ display: "flex", gap: 2.25, mt: 0.875 }}>
              {figures.map(({ label, value, mark }) => (
                <div key={label}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {mark}
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                  >
                    {currency.format(value)}
                  </Typography>
                </div>
              ))}
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.875,
                color: "text.secondary",
                ...TABULAR_NUMERALS,
              }}
            >
              {describeSettlement(point)}
            </Typography>
            <SettlementRangeStrip
              partyEv={point.partyEv}
              opponentRangeEnd={point.opponentRangeEnd}
              mid={point.mid}
              settledFigure={point.settledFigure}
              tone={tone}
            />
          </>
        );
      }}
    />
  );
};
